import { chartsOrder } from '@dashboard/constants/order';
import * as moment from 'moment';

export function dateFormat(date, format = 'YYYY-MM-DD HH:mm:ss') {
  return moment(date).format(format);
}

export function padTo2Digits(num) {
  return num.toString().padStart(2, '0');
}

export function convertSecondsToTime(time) {
  const sfactor = Number(time);
  const days = Math.floor(sfactor / (3600 * 24));
  const hours = Math.floor((sfactor % (3600 * 24)) / 3600);
  const minutes = Math.floor((sfactor % 3600) / 60);
  const seconds = Math.floor(sfactor % 60);

  return `${days}d ${padTo2Digits(hours)}h ${padTo2Digits(
    minutes,
  )}m ${padTo2Digits(seconds)}s`;
}

export function convertHoursToTime(time) {
  return convertSecondsToTime(time * 60 * 60);
}

export const parseUsageData = (currentStat, t, meta) => {
  if (currentStat) {
    const stats = {};
    currentStat.dates.forEach((date) => {
      if (stats[date.date] === undefined)
        stats[date.date] = { date: date.date };
      date.data.forEach((item) => {
        const keyName = t(item.name);

        stats[date.date][keyName] =
          meta && meta.subparse ? meta.subparse(item.value) : item.value;
      });
    });
    return Object.values(stats);
  }
  return null;
};

export const parseUsageDataByDay = (currentStat, t, meta) => {
  if (currentStat) {
    const days = meta.daysOfWeek.reduce((a, v) => ({ ...a, [t(v)]: 0 }), {});

    const stats = {};
    currentStat.dates.forEach((date) => {
      date.data.forEach((item) => {
        if (stats[item.name] === undefined)
          stats[item.name] = { time: item.name, ...days };
        const dayOfWeek =
          moment(date.date).day() > 0 ? moment(date.date).day() - 1 : 6;
        const index = meta.daysOfWeek[dayOfWeek];
        const keyName = t(index);

        stats[item.name][keyName] += item.value;
      });
    });

    return Object.values(stats);
  }
  return null;
};

export const rand = () => {
  return Math.floor(Math.random() * 6) + 1;
};

export const parseResultsData = (currentStat, t, meta) => {
  if (currentStat) {
    const stats = {};
    const totalDates = currentStat.dates.length;
    currentStat.dates.forEach((date) => {
      const totalItems = date.data.length;
      date.data.forEach((item, index) => {
        const keyIndex = `${item.unit}_${date.date}`;
        stats[keyIndex] = {
          date: date.date,
          unit: t(item.unit, meta.translation_ns_parse),
          metaData: JSON.stringify({
            isFirst: index === 0,
            isLast: index >= totalItems - 1 || totalItems <= 1,
            isMiddle: index === Math.floor(totalItems / 2),
            totalItems,
            date: date.date,
            totalDates,
            unit: t(item.unit, meta.translation_ns_parse),
          }),
        };

        item.data.forEach((key) => {
          const keyName = t(key.name);
          if (stats[keyIndex][keyName] === undefined)
            stats[keyIndex][keyName] = 0;
          stats[keyIndex][keyName] += key.value;
        });
      });
    });

    return Object.values(stats);
  }
  return null;
};

export const parseResultsDataGrouped = (currentStat, t) => {
  if (currentStat) {
    const stats = {};
    currentStat.dates.forEach((date) => {
      if (stats[date.date] === undefined)
        stats[date.date] = { date: date.date };
      date.data.forEach((item) => {
        item.data.forEach((key) => {
          const keyName = t(key.name);

          if (stats[date.date][keyName] === undefined)
            stats[date.date][keyName] = 0;
          stats[date.date][keyName] += key.value;
        });
      });
    });

    return Object.values(stats);
  }
  return null;
};

export const parseAnalysisData = (currentStat, t, meta) => {
  if (currentStat) {
    const stats = {};
    currentStat.dates.forEach((date) => {
      date.data?.forEach((item) => {
        const totalItems = item.subprocesses.length;
        item.subprocesses?.forEach((subprocess, index) => {
          if (stats[subprocess.subprocess] === undefined) {
            stats[subprocess.subprocess] = {
              subprocess: t(subprocess.subprocess, meta.translation_ns_parse),
              cognitiveProcess: t(
                item.cognitiveProcess,
                meta.translation_ns_parse,
              ),
              metaData: JSON.stringify({
                isFirst: index === 0,
                isLast: index >= totalItems - 1,
                isMiddle: index === Math.floor(totalItems / 2),
                totalItems,
                date: date.date,
                subprocess: t(subprocess.subprocess, meta.translation_ns_parse),
                cognitiveProcess: t(
                  item.cognitiveProcess,
                  meta.translation_ns_parse,
                ),
              }),
            };
          }
          subprocess.data?.forEach((result) => {
            const keyName = t(result.name);
            if (stats[subprocess.subprocess][keyName] === undefined)
              stats[subprocess.subprocess][keyName] = 0;

            stats[subprocess.subprocess][keyName] += result.value;
          });
        });
      });
    });

    return Object.values(stats);
  }
  return null;
};

export const parseNeuroDevData = (currentStat, t, meta) => {
  if (currentStat) {
    const stats = {};
    currentStat.dates.forEach((date) => {
      date.data?.forEach((item) => {
        const process = t(item.cognitiveProcess, meta.translation_ns_parse);
        if (stats[process] === undefined) {
          stats[process] = {
            cognitiveProcess: t(process),
          };
        }

        item.data?.forEach((result) => {
          const keyName = t(result.name);
          if (stats[process][keyName] === undefined)
            stats[process][keyName] = 0;
          stats[process][keyName] += result.value;
        });
      });
    });

    return Object.values(stats);
  }
  return null;
};

export const parseAnalysisStyleData = (currentStat, t, meta) => {
  if (currentStat) {
    return currentStat.dates.map((date) => {
      const stat = {
        date: date.date,
      };

      date.data.forEach((result) => {
        stat[t(result.name, meta.translation_ns_parse)] = result.value;
      });

      return stat;
    });
  }
  return null;
};

export const parseMetacognitiveData = (currentStat, t, meta) => {
  if (currentStat) {
    const stats = {};
    currentStat.dates.forEach((date) => {
      const totalItems = date.data.length;
      date.data?.forEach((item, index) => {
        const question = t(item.question, meta.translation_ns_parse);
        const key = `${question}  -   ${date.date}`;
        if (stats[key] === undefined) {
          stats[key] = {
            date: date.date,
            key,
            question,
            metaData: JSON.stringify({
              isFirst: index === 0,
              isLast: index >= totalItems - 1,
              isMiddle: index === Math.floor(totalItems / 2),
              totalItems,
              date: date.date,
            }),
          };
        }
        item.data?.forEach((result) => {
          const keyName = t(result.name);
          if (stats[key][keyName] === undefined) stats[key][keyName] = 0;
          stats[key][keyName] += result.value;
        });
      });
    });

    return Object.values(stats);
  }
  return null;
};

let lastX = 0;
export const tickHorizontalGroup = (tickProps, HEIGHT, angle) => {
  const node = [];
  const { x, y, payload, visibleTicksCount, width } = tickProps;
  const { value, offset } = payload;
  const horizontalSpace = Math.abs(lastX - x) - offset;

  const metaData = JSON.parse(value);
  const xLabel = x - 20;
  if (metaData.isMiddle) {
    if (angle) {
      node.push(
        <text
          key={`label_${metaData.cognitiveProcess}`}
          x={xLabel}
          y={y}
          fontSize={11}
          textAnchor="middle"
          transform={`rotate(${angle}, ${xLabel}, ${y})`}
        >{`${metaData.date}`}</text>,
      );
    } else if (metaData.totalItems <= 1) {
      const pathXPLUS = Math.floor(lastX + horizontalSpace);
      const dateWidth = width / visibleTicksCount / 2;

      node.push(
        <path
          d={`M${pathXPLUS},${y - 30}v${-(HEIGHT - 22)}`}
          strokeWidth={1}
          stroke="grey"
        />,
      );

      node.push(
        <text
          x={x + dateWidth}
          y={y - 40}
          fontSize={11}
          textAnchor="middle"
        >{`${metaData.date}`}</text>,
      );
    } else {
      node.push(
        <text
          x={x}
          y={y - 40}
          fontSize={11}
          textAnchor="middle"
        >{`${metaData.date}`}</text>,
      );
    }
  }

  if (metaData.isLast && metaData.totalItems > 1) {
    const pathX = Math.floor(x + horizontalSpace);
    lastX = 0;
    node.push(
      <path
        d={`M${pathX},${y - 30}v${-(HEIGHT - 22)}`}
        strokeWidth={1}
        stroke="grey"
      />,
    );
  }

  lastX = x;

  return node;
};

let lastY = 0;
export const tickVerticalGroup = (tickProps) => {
  const node = [];

  const { x, y, payload } = tickProps;
  const { value, offset } = payload;
  const verticalSpace = Math.abs(lastY - y) - offset;
  lastY = y;
  const metaData = JSON.parse(value);
  const xLabel = x - 20;
  if (metaData.isMiddle) {
    node.push(
      <text
        key={`label_${metaData.cognitiveProcess}`}
        x={xLabel}
        y={y}
        fontSize={11}
        textAnchor="middle"
        transform={`rotate(-90, ${xLabel}, ${y})`}
      >{`${metaData.cognitiveProcess}`}</text>,
    );
  }

  if (metaData.isLast) {
    lastY = 0;
    node.push(
      <line
        key={`line_${metaData.cognitiveProcess}`}
        x1={xLabel}
        y1={y + verticalSpace}
        x2={x + 208}
        y2={y + verticalSpace}
        strokeWidth={1}
        stroke="grey"
      />,
    );
  }
  return node;
};

export const sortCharts = (chart1, char2) => {
  const order1 = chartsOrder[chart1.name] || 99;
  const order2 = chartsOrder[char2.name] || 99;

  return order1 - order2;
};

export const makeUID = (length = 6) => {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const numberFormat = (
  number,
  locale = 'es-ES',
  options = {
    style: 'decimal',
    useGrouping: 'always',
  },
) => {
  const formatter = new Intl.NumberFormat(locale, options);

  return formatter.format(number);
};
